/**
 * Camera assistance for an ambiguous tap.
 *
 * When `snapResolve` reports contenders, the projection has genuinely thrown
 * away the information needed to answer — St Lucia and St Vincent are 1.5 CSS
 * pixels apart at the default view. The only honest recoveries are "guess" and
 * "get closer", and this module does the second: it computes the camera that
 * puts a full exclusive touch target under every contender, so the player's
 * next tap cannot be a lottery.
 *
 * WHAT THIS DOES NOT DO. It does not name anything (invariant 7 — there is no
 * text anywhere in this package), it does not stop the clock (it is a 260ms
 * ease inside the round, and the round timer is the server's anyway —
 * invariant 2), and it does not leak (invariant 5): the framing is a function
 * of where the FINGER landed and of static geography, never of the round's
 * target. Tapping the Caribbean magnifies the Caribbean whether the answer is
 * Grenada, Peru or Mongolia.
 */

import type { LngLatTuple } from './geo';
import { angularDistanceDeg, sphericalMean } from './geo';
import { zoomForPxPerDeg } from './projection';
import { pickTarget } from './targets';

export interface MagnifyPlan {
  readonly center: LngLatTuple;
  readonly zoom: number;
}

export interface MagnifyOptions {
  /** Screen separation to give the closest pair. `SNAP.separationTargetPx`. */
  readonly separationTargetPx: number;
  /** Never zoom by less than this, so every ambiguous tap makes progress. */
  readonly minZoomStep: number;
  readonly maxZoom: number;
}

/**
 * Where to put the camera so `contenders` become individually tappable, or
 * null when there is nothing useful left to do — fewer than two contenders, an
 * unknown code, or the camera is already at maximum zoom (in which case the
 * caller must commit to its best guess rather than spin).
 */
export function magnifyPlan(
  contenders: readonly string[],
  currentZoom: number,
  opts: MagnifyOptions,
): MagnifyPlan | null {
  if (contenders.length < 2) return null;
  if (currentZoom >= opts.maxZoom - 1e-6) return null;

  const anchors: LngLatTuple[] = [];
  for (const code of contenders) {
    const t = pickTarget(code);
    if (t) anchors.push([t.anchor[0], t.anchor[1]]);
  }
  if (anchors.length < 2) return null;

  let minSepDeg = Number.POSITIVE_INFINITY;
  for (let i = 0; i < anchors.length; i++) {
    for (let j = i + 1; j < anchors.length; j++) {
      const d = angularDistanceDeg(anchors[i] as LngLatTuple, anchors[j] as LngLatTuple);
      if (d < minSepDeg) minSepDeg = d;
    }
  }

  // Two anchors on top of each other would demand infinite zoom. Clamping to
  // maxZoom and letting the caller commit is better than a camera that hunts.
  const needed =
    minSepDeg > 1e-4
      ? zoomForPxPerDeg(opts.separationTargetPx / minSepDeg)
      : Number.POSITIVE_INFINITY;

  const zoom = Math.min(opts.maxZoom, Math.max(currentZoom + opts.minZoomStep, needed));
  return { center: sphericalMean(anchors), zoom };
}
